import { Checkbox, Text, Title3, makeStyles } from '@fluentui/react-components';
import { getStepType, type ConfigOf, type SubmissionOf } from '@vigeo/shared';
import { tokens } from '@vigeo/ui';
import type { StepPortalProps, StepReviewProps, StepUiModule } from '../contract.js';

const useStyles = makeStyles({
  block: { display: 'flex', flexDirection: 'column', gap: tokens.spacingVerticalM },
  body: { whiteSpace: 'pre-wrap' },
});

/**
 * Each half parses its own props with the kernel's own schema rather than casting.
 * The registry hands these components `unknown`, and the schema that already defines the
 * shape is the honest way back to a concrete type — a cast here would be a second,
 * unchecked declaration of what a config is.
 */
const kernel = getStepType('info-task');
type Config = ConfigOf<'info-task'>;
type Submission = SubmissionOf<'info-task'>;

function InfoTaskPortal({ config, draft, onDraftChange, disabled }: StepPortalProps) {
  const styles = useStyles();
  const parsed = kernel.configSchema.safeParse(config);
  if (!parsed.success) return <Text>This step is misconfigured. HR has been notified.</Text>;

  const settings: Config = parsed.data;
  const acknowledged = kernel.submissionSchema.safeParse(draft).success;

  return (
    <div className={styles.block}>
      <Title3>{settings.heading}</Title3>
      <Text className={styles.body}>{settings.bodyText}</Text>
      {settings.requiresConfirmation ? (
        <Checkbox
          label="I have read this"
          checked={acknowledged}
          disabled={disabled}
          onChange={(_, d) =>
            onDraftChange(d.checked === true ? ({ readAt: new Date().toISOString() } satisfies Submission) : null)
          }
        />
      ) : null}
    </div>
  );
}

function InfoTaskReview({ config, submission }: StepReviewProps) {
  const styles = useStyles();
  const settings = kernel.configSchema.safeParse(config);
  const answer = kernel.submissionSchema.safeParse(submission);

  return (
    <div className={styles.block}>
      <Text weight="semibold">{settings.success ? settings.data.heading : 'Information'}</Text>
      <Text>
        {answer.success
          ? `Read ${new Date(answer.data.readAt).toLocaleString()}`
          : 'No acknowledgment recorded.'}
      </Text>
    </div>
  );
}

export const infoTaskUi: StepUiModule = { portal: InfoTaskPortal, review: InfoTaskReview };
